import { FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { httpServices as services } from "@/services";
import type { StaffDetail } from "@/types/domain";
import { ApiClientError } from "@/services/http/client";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ErrorState, LoadingState } from "@/components/shared/StatePanels";

const fieldClass = "space-y-2";

export default function EditStaffPage() {
  const { staffId = "" } = useParams();
  const navigate = useNavigate();
  const [person, setPerson] = useState<StaffDetail>();
  const [error, setError] = useState("");
  const [form, setForm] = useState({ job_title: "", department: "", employment_type: "", employment_status: "ACTIVE", hire_date: "", phone: "", email: "" });

  useEffect(() => {
    services.staff.get(staffId).then((item) => {
      if (!item) { setError("Staff record was not found."); return; }
      setPerson(item);
      setForm({
        job_title: item.jobTitle || "",
        department: item.department || "",
        employment_type: item.employmentType || "",
        employment_status: item.employmentStatus || "ACTIVE",
        hire_date: item.hireDate || "",
        phone: item.phone || "",
        email: item.email || "",
      });
    }).catch(() => setError("Unable to load staff record."));
  }, [staffId]);

  const set = (field: keyof typeof form, value: string) => setForm((prev) => ({ ...prev, [field]: value }));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!person) return;
    try {
      const changes = { ...form, hire_date: form.hire_date || undefined, phone: form.phone || undefined, email: form.email || undefined };
      const result = await services.staff.update(person.id, changes);
      toast("Employee updated", { description: `${result.name} has been saved.` });
      navigate(`/staff/${result.id}`);
    } catch (reason) {
      const message = reason instanceof ApiClientError ? reason.payload.message : reason instanceof Error ? reason.message : "Unable to save changes.";
      toast("Employee not updated", { description: message });
    }
  };

  if (error) return <ErrorState message={error} />;
  if (!person) return <LoadingState label="Loading employee for editing" />;

  return (
    <div className="space-y-6" data-testid="edit-staff-page">
      <PageHeader eyebrow="HR administration" title="Edit Employee" description={`${person.name} · ${person.employeeCode}`} backTo={`/staff/${person.id}`} />
      <form onSubmit={submit} className="space-y-5" data-testid="edit-staff-form">
        <Card>
          <CardHeader><CardTitle>Employment details</CardTitle></CardHeader>
          <CardContent className="grid gap-5 md:grid-cols-2">
            <div className={fieldClass}><Label htmlFor="job_title">Job title</Label><Input id="job_title" value={form.job_title} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("job_title", e.target.value)} data-testid="edit-staff-job-title-input" /></div>
            <div className={fieldClass}><Label htmlFor="department">Department</Label><Input id="department" value={form.department} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("department", e.target.value)} data-testid="edit-staff-department-input" /></div>
            <div className={fieldClass}><Label>Employment type</Label><Select value={form.employment_type} onValueChange={(v: string) => set("employment_type", v)}><SelectTrigger data-testid="edit-staff-employment-type-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Full time">Full time</SelectItem><SelectItem value="Part time">Part time</SelectItem><SelectItem value="Contract">Contract</SelectItem><SelectItem value="Consultant">Consultant</SelectItem></SelectContent></Select></div>
            <div className={fieldClass}><Label>Employment status</Label><Select value={form.employment_status} onValueChange={(v: string) => set("employment_status", v)}><SelectTrigger data-testid="edit-staff-employment-status-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="ACTIVE">Active</SelectItem><SelectItem value="INACTIVE">Inactive</SelectItem><SelectItem value="SUSPENDED">Suspended</SelectItem><SelectItem value="TERMINATED">Terminated</SelectItem></SelectContent></Select></div>
            <div className={fieldClass}><Label htmlFor="hire_date">Hire date</Label><Input id="hire_date" type="date" value={form.hire_date} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("hire_date", e.target.value)} data-testid="edit-staff-hire-date-input" /></div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Contact</CardTitle></CardHeader>
          <CardContent className="grid gap-5 md:grid-cols-2">
            <div className={fieldClass}><Label htmlFor="phone">Phone</Label><Input id="phone" type="tel" value={form.phone} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("phone", e.target.value)} data-testid="edit-staff-phone-input" /></div>
            <div className={fieldClass}><Label htmlFor="email">Email</Label><Input id="email" type="email" value={form.email} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("email", e.target.value)} data-testid="edit-staff-email-input" /></div>
          </CardContent>
        </Card>
        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(`/staff/${person.id}`)} data-testid="edit-staff-cancel-button">Cancel</Button>
          <Button type="submit" data-testid="edit-staff-submit-button"><Save className="mr-2 size-4" />Save changes</Button>
        </div>
      </form>
    </div>
  );
}
